import React, {useState} from 'react';
import cn from 'classnames';
import moment from 'moment';
import styled from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {toast} from 'react-toastify';
import {DashboardContent} from 'components/DashboardContent/styles';
import Body3 from 'components/typography/Body3';
import {REPORT_DATEPICKER_INDEX, REPORT_TYPES} from 'helpers/constants/reports';
import IconDisableSurvey from 'assets/IconDisableSurvey';
import MoreActionsMenu from 'components/MoreActionsMenu';
import DialogDisableSurvey from 'routes/App/feedback/components/modals/DisableSurvey';
import {DisableContainer, UnderlineButton} from 'routes/App/feedback/styles';
import {useBannerSlot} from 'components/BannerStack/BannerStackContext';
import BANNER_STACK_PRIORITY from 'components/BannerStack/priorities';
import {
  SET_FEEDBACK_STATUS,
  SET_FEEDBACK_STATUS_SUCCESS
} from 'store/global/user/action';
import {
  FeedbackHeader,
  FeedbackHeaderActions,
  FeedbackSubtitle,
  FeedbackTab,
  FeedbackTabs,
  FeedbackTitle
} from 'routes/App/feedback/redesign/styles';
import ReviewsTab from './ReviewsTab';
import InsightsTab from './InsightsTab';

const OuterTabs = styled(FeedbackTabs)`
  border-bottom: 2px solid #d9d9d9;
`;

const TOP_TABS = [
  {id: 'reviews', label: 'Reviews'},
  {id: 'insights', label: 'Insights'}
];

const CustomerFeedback = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const activeLocation = useSelector((state) => state.user?.activeLocation);
  const isSurveyActivated = activeLocation?.isSurveyActivated;
  const hasReceivedSurveys = activeLocation?.hasReceivedSurveys;
  const locationTimezone = activeLocation?.Timezone;
  const showDisabledBanner = !isSurveyActivated && hasReceivedSurveys;
  const bannerStackIndex = useBannerSlot(
    'surveyDisabled',
    BANNER_STACK_PRIORITY.surveyDisabled,
    showDisabledBanner
  );

  const [activeTopTab, setActiveTopTab] = useState('reviews');
  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);

  const enableSurvey = () => {
    dispatch(
      SET_FEEDBACK_STATUS({
        isSurveyActivated: true,
        onSuccess: () => {
          dispatch(SET_FEEDBACK_STATUS_SUCCESS(true));
          toast.success('Survey has been enabled successfully!');
        },
        onError: () => {
          toast.error('Something went wrong! Please try again later!');
        }
      })
    );
  };

  const handleExportReport = () => {
    const today = moment()
      .utcOffset(locationTimezone || 0, false)
      .utc();
    const endDate = today.format('YYYY-MM-DD');
    const startDate = today.clone().subtract(30, 'days').format('YYYY-MM-DD');
    history.push({
      pathname: '/reports',
      search: `?type=${REPORT_TYPES.FEEDBACK_SUMMARY}`,
      state: {
        fromFeedback: true,
        datePickerConfig: {
          startDate,
          endDate,
          datePickerSelectIndex: REPORT_DATEPICKER_INDEX.CUSTOM_RANGE
        }
      }
    });
  };

  return (
    <DashboardContent className='DashboardContent'>
      {showDisabledBanner && (
        <DisableContainer style={{'--banner-stack-index': bannerStackIndex}}>
          <IconDisableSurvey />
          <Body3 className='White'>
            Your review survey is currently disabled, new data is not coming in.
            <UnderlineButton onClick={enableSurvey}>
              Click to enable
            </UnderlineButton>
          </Body3>
        </DisableContainer>
      )}
      <FeedbackHeader className='AlwaysFlex'>
        <div>
          <FeedbackTitle>Customer Reviews</FeedbackTitle>
          <FeedbackSubtitle>
            Track customer reviews and manage responses in one place.
          </FeedbackSubtitle>
        </div>
        <FeedbackHeaderActions>
          <MoreActionsMenu
            options={[
              {label: 'Export full report', onClick: handleExportReport},
              isSurveyActivated
                ? {
                    label: 'Disable Customer Survey',
                    onClick: () => setIsDisableModalOpen(true)
                  }
                : {label: 'Reactivate Customer Survey', onClick: enableSurvey}
            ]}
          />
        </FeedbackHeaderActions>
      </FeedbackHeader>

      <OuterTabs>
        {TOP_TABS.map((tab) => (
          <FeedbackTab
            key={tab.id}
            className={cn({isActive: activeTopTab === tab.id})}
            onClick={() => setActiveTopTab(tab.id)}
          >
            {tab.label}
          </FeedbackTab>
        ))}
      </OuterTabs>

      {activeTopTab === 'reviews' ? <ReviewsTab /> : <InsightsTab />}

      <DialogDisableSurvey
        isOpenDisableModal={isDisableModalOpen}
        setIsOpenDisableModal={setIsDisableModalOpen}
        Title='Turn off review surveys?'
        Description='Most merchants keep this on to hear from customers and improve reviews.'
      />
    </DashboardContent>
  );
};

export default CustomerFeedback;
